import React, { createContext, useContext, useEffect, useState } from "react";
import { axiosInstance } from "../api/config";
import { getAuthErrorMessage } from "../utils/authErrors";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [authUser, setAuthUser] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const isAuthenticated = !!authUser;

  const normalizeMeResponse = (data) => data?.user || null;

  useEffect(() => {
    const initAuth = async () => {
      console.log('Initializing auth via /auth/me...');
      try {
        const me = await axiosInstance.get('/auth/me');
        const userData = normalizeMeResponse(me.data);
        setAuthUser(userData);
        setIsAuthLoading(false);
      } catch (e) {
        setAuthUser(null);
        setIsAuthLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axiosInstance.post('/auth/login', {
        email,
        password,
      });
      
      const userData = normalizeMeResponse(response.data);
      setAuthUser(userData);
      
      // Backend handles token management automatically
      setAuthError(null);
      console.log('Login successful');
      return userData;
      
    } catch (error) {
      console.error('Login failed:', error);
      const errorMessage = getAuthErrorMessage(error, "Login failed");
      setAuthError(errorMessage);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await axiosInstance.post('/auth/logout');
    } catch (error) {
      console.error('Logout request failed:', error);
    } finally {
      setAuthUser(null);
      console.log('Logout completed');
    }
  };

  const signUp = async (email, password, username) => {
    try {
      const response = await axiosInstance.post('/auth/signup', {
        email,
        password,
        username
      });
      
      setAuthError(null);
      return response.data;
    } catch (error) {
      console.error('Sign-up failed:', error);
      const errorMessage = getAuthErrorMessage(error, "Sign-up failed");
      setAuthError(errorMessage);
      throw error;
    }
  };

  const verifyEmail = async (email, code) => {
    try {
      const response = await axiosInstance.post('/auth/verify', {
        email,
        code,
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      setAuthError(null);
      return response.data;
    } catch (error) {
      console.error('Verification failed:', error);
      const errorMessage = getAuthErrorMessage(error, "Verification failed");
      setAuthError(errorMessage);
      throw error;
    }
  };

  const resendVerificationCode = async (email) => {
    const response = await axiosInstance.post("/auth/resend-verification", {
      email: String(email || "").trim().toLowerCase(),
    });
    return response.data;
  };

  const forgotPassword = async (email) => {
    const response = await axiosInstance.post("/auth/forgot-password", { email });
    return response.data;
  };

  const resetPassword = async (email, code, password) => {
    const response = await axiosInstance.post("/auth/reset-password", { email, code, password });
    return response.data;
  };

  const refreshMe = async () => {
    const me = await axiosInstance.get("/auth/me");
    const userData = normalizeMeResponse(me.data);
    setAuthUser(userData);
    return userData;
  };

  return (
    <AuthContext.Provider value={{ 
      currentUser: authUser,
      authUser,
      login, 
      logout, 
      signUp,
      verifyEmail,
      resendVerificationCode,
      forgotPassword,
      resetPassword,
      refreshMe,
      loading: isAuthLoading,
      isAuthLoading,
      isAuthenticated,
      authError
    }}>
      {children}
    </AuthContext.Provider>
  );
};